/**
 * Tracked links: get-otii.com/go/<slug>, which counts the click and sends
 * the person on to an Otii web page with UTM tags. Shared so the web form
 * previews exactly the address the server will redirect to.
 */

export const LINK_CHANNELS = ["whatsapp", "facebook", "instagram", "linkedin", "linkedin_dm", "email", "direct_message", "newsletter", "qr", "print", "event"] as const;
export type LinkChannel = (typeof LINK_CHANNELS)[number];

export const LINK_CHANNEL_LABEL: Record<LinkChannel, string> = {
  whatsapp: "WhatsApp",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn post",
  linkedin_dm: "LinkedIn message",
  email: "Email",
  direct_message: "Direct message",
  newsletter: "Newsletter",
  qr: "QR code",
  print: "Print (typed address)",
  event: "Event",
};

/**
 * Per-person links only on one-to-one channels. A group link gets forwarded
 * and screenshotted, so pinning a click on one person would be wrong.
 */
export const ONE_TO_ONE_CHANNELS: readonly LinkChannel[] = ["email", "direct_message", "linkedin_dm"];

/** Where a link goes when none is given, and where a dead or unknown link lands. */
export const LINK_FALLBACK_URL = "https://www.get-otii.com/";
/** Links only ever lead to Otii's own site, so /go/ can't be used as an open redirect. */
export const LINK_DESTINATION_HOSTS: readonly string[] = ["get-otii.com", "www.get-otii.com"];
/** The query parameter carrying a per-person token. link-attribution.js on the website reads and strips it. */
export const RECIPIENT_PARAM = "rt";
/** Every link's medium (the spec's choice), except QR codes: scans are told apart from clicks. */
export const LINK_MEDIUM = "share";
export const QR_MEDIUM = "qr";
/** Names the link itself on the landing address, so a form sent later in the visit says which link brought them. */
export const LINK_ID_PARAM = "utm_id";

/** lower-case, hyphenated, at most 40 characters. */
export function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40)
      .replace(/-+$/, "") || "link"
  );
}

export function isAllowedDestination(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === "https:" && LINK_DESTINATION_HOSTS.includes(u.hostname) && !u.username && !u.password;
  } catch {
    return false;
  }
}

export interface LinkUtms {
  source: string;
  medium: string;
  campaign: string;
  content: string | null;
}

/** The tags a new link gets: channel as the source, the campaign, and the label as the content unless given. */
export function linkUtms(input: { channel: LinkChannel; campaign: string; label: string; content?: string | null }): LinkUtms {
  return {
    source: input.channel === "qr" ? "print" : slugify(input.channel),
    medium: input.channel === "qr" ? QR_MEDIUM : LINK_MEDIUM,
    campaign: slugify(input.campaign),
    content: input.content?.trim() ? slugify(input.content) : slugify(input.label),
  };
}

/**
 * The site is served on www. The bare domain only forwards its home page
 * there and drops the path and query, tags included (checked 8 Oct 2026),
 * so links always land on www.
 */
export function canonicalDestination(url: string): string {
  const u = new URL(url);
  if (u.hostname === "get-otii.com") u.hostname = "www.get-otii.com";
  return u.toString();
}

/** The destination with the link's tags (replacing any already there), its slug as utm_id, plus the person's token on per-person links. */
export function taggedUrl(destination: string, utm: LinkUtms, recipientToken?: string | null, slug?: string | null): string {
  const u = new URL(canonicalDestination(destination));
  u.searchParams.delete(RECIPIENT_PARAM);
  u.searchParams.set("utm_source", utm.source);
  u.searchParams.set("utm_medium", utm.medium);
  u.searchParams.set("utm_campaign", utm.campaign);
  if (utm.content) u.searchParams.set("utm_content", utm.content);
  else u.searchParams.delete("utm_content");
  if (slug) u.searchParams.set(LINK_ID_PARAM, slug);
  else u.searchParams.delete(LINK_ID_PARAM);
  // A pseudonymous token only. Never a name or an email address in a URL.
  if (recipientToken) u.searchParams.set(RECIPIENT_PARAM, recipientToken);
  return u.toString();
}
